// Third-party
import Head from 'next/head';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

// First-party
import useForm from '@lib/hooks/form';
import { Slot, createPage } from '@lib/util/templateHelpers';
import UploadFileForm from '@/components/forms/UploadFileForm';
import gameApi from '@/apis/drupal/game';
import categoryApi from '@/apis/drupal/category';
import useNotifications from '@lib/hooks/notifications';

// Component lib
import shared from '@lib/components';
const { 
  Block, 
  Title, 
  Input, 
  Form, 
  Checkbox, 
  Textarea, 
  Button, 
  Content,
  Box,
  DocumentTitle
} = shared;

// Local
import styles from './index.module.scss';
import DeveloperLayout from '@/layouts/DeveloperLayout';

const schema = {
  gameLink: {
    schema: {
      type: Form.FIELD_ANY,
      required: true
    }
  },
  title: {
    schema: {
      type: Form.FIELD_STRING,
      required: true,
      minLength: 2,
    }
  },
  body: {
    schema: {
      type: Form.FIELD_ANY,
      required: true
    }
  },
  categories: {
    value: [],
    schema: {
      type: Form.FIELD_LIST,
      acceptedValues: [
        { value: 'battle-royal', label: 'Battle Royal' },
        { value: 'power-ups', label: 'Power-ups' },
        { value: 'upgrades', label: 'Upgrades' },
        { value: 'team', label: 'Team' },
        { value: 'zombies', label: 'Zombies' },
        { value: 'tank', label: 'Tank' },
        { value: 'free-for-all', label: 'Free For All' },
        { value: 'moomoo.io-style', label: 'Moomoo.io Style' },
        { value: 'racing', label: 'Racing' },
        { value: 'agario-style', label: 'Argario Style' },
        { value: 'sports', label: 'Sports' },
        { value: 'strategy', label: 'Strategy' },
        { value: 'space', label: 'Space' },
        { value: 'splix-style', label: 'Splix Style' },
        { value: 'snake-games', label: 'Snake Games' },
        { value: 'diep-style', label: 'Diep Styles' },
        { value: 'classic', label: 'Classic' },
        { value: 'bejeweled-style', label: 'Bejeweled Style' },
        { value: 'maze', label: 'Maze' },
        { value: 'rpg', label: 'RPG' },
        { value: 'shooter', label: 'Shooter' },
        { value: 'tower-defense', label: 'Tower Defense' },
        { value: 'ocean', label: 'Ocean' },
        { value: 'asteroids', label: 'Asteroids' }
      ],
      minRequired: 1,
    }
  },
  crossPromotion: {
    value: false,
    schema: {
      trueValue: true,
      falseValue: false,
      type: Form.BOOLEAN
    }
  },
  ioLink: {
    value: false,
    schema: {
      trueValue: true,
      falseValue: false,
      type: Form.BOOLEAN
    }
  },
  mobileTrue: {
    value: false,
    schema: {
      trueValue: true,
      falseValue: false,
      type: Form.BOOLEAN
    }
  },
  amazonLink: {
    schema: {
      type: Form.FIELD_ANY
    }
  },
  googlePlayLink: {
    schema: {
      type: Form.FIELD_ANY
    }
  },
  appleAppLink: {
    schema: {
      type: Form.FIELD_ANY
    }
  }
};

export default createPage('AddGamePage', { styles }, function AddGamePage ({ className }) {
  const router = useRouter();
  const { notify } = useNotifications();
  const form = useForm(schema);
  const [ thumbnailData, setThumbnailData ] = useState({});
  const [ promoData, setPromoData ] = useState({});
  const [ videoThumbnailData, setVideoThumbnailData ] = useState({});
  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    (async () => {
      const categoriesApi = await categoryApi.listAll();

      const newCategories = categoriesApi.map((category) => {
        return { value: category.tid, label: category.name }
      });

      //Sets form schema category values
      form.schema.categories.schema.acceptedValues = newCategories;
      form.resetForm()

      // TODO: remove
      form.setData({
        mobileTrue: true
      });
    })();
  }, []);

  const submit = async () => {
    if (Object.keys(thumbnailData).length === 0) {
      notify('A thumbnail must be uploaded before you can submit your game.');
      return;
    }

    setLoading(true);

    const { data, hasErrors } = form.validate();
    if (hasErrors) {
      setLoading(false);
      return;
    }

    const submittedCategories = data.categories.map((category_id)=>{
      return { target_id: category_id };
    });
    
    const submittedData = {
      type: [ {'target_id': 'game'} ], 
      title: [ {'value': data.title} ],
      body: [
        {
          'value': data.body,
          'format': 'full_html',
          'summary': ''
        }
      ],
      field_category: submittedCategories,
      field_link: [ 
        {'value': data.gameLink}
      ],
      field_mobile: [
        { 'value': data.mobileTrue }
      ],
      field_cross_promotion: [
        {'value': data.crossPromotion}
      ],
      field_more_io_games: [
        {'value': data.ioLink}
      ],
      field_thumbnail: [ //Required
        {
          'target_id': thumbnailData.fid[0].value,
          'alt': thumbnailData.filename[0].value,
          'title': thumbnailData.filename[0].value,
        }
      ],
      // If videoThumbnailData has state
      field_video_thumbnail: [
        {'target_id': Object.keys(videoThumbnailData).length != 0 ? videoThumbnailData.fid[0].value : 0 }
      ],
      field_amazon_store_link: [
        {'value': data.amazonLink}
      ],
      field_apple_app_store_link: [
        {'value': data.appleAppLink}
      ],
      field_google_play_store_link: [
        {'value': data.googlePlayLink}       
      ],
    };

    if (Object.keys(promoData).length) {
      submittedData.field_promo_image = [
        {
          'target_id': promoData.fid[0].value,
          'alt': promoData.filename[0].value,
          'title': promoData.filename[0].value,
        }
      ]
    }
    
    const gameResult = await gameApi.publishGame(submittedData);

    if (!gameResult || gameResult.error) {
      notify('There was a problem submitting your game. Please try again later.');
      setLoading(false);
    } else {
      notify('Your game has been submitted!');
      router.push('/developer');
    } 
  };

  return (
    <>
      <DocumentTitle title='Create Game' />

      <DeveloperLayout className={className}>
        <Box>
          <Block>
            <Block>
              <Title>Create Game</Title>
            </Block>
          </Block>

          <br />
          <Block>
            <Title size4>Add Game Thumbnail</Title>
            <Block>
              <Content>
                <p>
                  Note: A game thumbnail must be added before you are able to submit any other game information
                </p>
                <p>
                  A thumbnail image of your game. 
                  Files must be less than <b>300KB</b>. 
                  Allowed file types: <b>png jpg jpeg</b>.
                  Images must be exactly <b>275x157</b> pixels.
                </p>
              </Content>
            </Block>
            <Block>
              <UploadFileForm
                accept={Input.FILE_ACCEPT_IMAGE}
                onSuccess={(result) => setThumbnailData(result)}
                onError={(error) => console.warn('ERROR', error)}
                maxFileSize={300 * 1024}
                imageSize={[275, 157]}
              >
                Upload Thumbnail
              </UploadFileForm>
            </Block>
          </Block>

          <br />
          <Block>
            <Title size4>Add Promo Image</Title>
            <Block>
              <Content>
                <p>
                  A promo image is recommended if you want to 'feature' your game. If you do not include an image and attempt to feature a game, we will use the file in the Game Thumbnail field which may result in a lower quality image and therefore less engagement. Please note that the image must be uploaded before you attempt to feature your game and cannot be appended later.
                </p>
                <p>
                  A large promotional image of your game used when your game is featured. 
                  Files must be less than <b>300KB</b>.
                  Allowed file types: <b>png jpg jpeg</b>.
                  Images must be exactly <b>364x208</b> pixels.
                </p>
              </Content>
            </Block>
            <Block>
              <UploadFileForm
                accept={Input.FILE_ACCEPT_IMAGE}
                onSuccess={(result) => setPromoData(result)}
                onError={(error) => console.warn('ERROR', error)}
                maxFileSize={300 * 1024}
                imageSize={[364, 208]}
              >
                Upload Promo Image
              </UploadFileForm>
            </Block>
          </Block>

          <br />
          <Block>
            <Title size4>Add Video Thumbnail</Title>
            <Block>
              <Content>
                <p>
                  Video thumbnail which displays when the user hovers over the game thumbnail with their mouse. 
                  Should be 275x157 pixels, or the same aspect ratio. 
                  We strongly recommend speeding the footage up 2x and zooming in, if necessary, to see the action.
                </p>
                <p>
                  Files must be less than <b>500KB</b>.
                  Allowed file types: <b>mp4</b>.
                </p>
              </Content>
            </Block>
            <Block>
              <UploadFileForm
                accept={'video/mp4'}
                onSuccess={(result) => setVideoThumbnailData(result)}
                onError={(error) => console.warn('ERROR', error)}
                maxFileSize={500 * 1024}
                videoSize={[275, 157]}
              >
                Upload Thumbnail
              </UploadFileForm>
            </Block>
          </Block>

          <br />
          <Block>
            <Form onSubmit={submit}>
              <Title size4>General</Title>

              <Block>
                <Input.Field name='gameLink' type={Input.ANY} form={form}>
                  <Slot name='label'>Game URL (HTTPS Only)</Slot>
                </Input.Field>
                <p>
                  <strong>MUST BE HTTPS TO BE APPROVED.</strong> HTTP-only games no longer show in iogames.space listings.
                </p>
              </Block>

              <Block>
                <Input.Field name='title' type={Input.ANY} form={form}>
                  <Slot name='label'>Title</Slot>
                </Input.Field>
              </Block>

              <Block>
                <Textarea.Field name='body' form={form}>
                  <Slot name='label'>Description</Slot>
                </Textarea.Field>
                <Content>
                  <p><b>Warning:</b> Your description must be Unique, meaning it cannot be found elsewhere on the internet. All descriptions will be checked for uniqueness before approval.</p>
                  <p>Important: please start the description of your game, the first sentence, with concise and accurate overview of the game. '[game name] is'</p>
                  <p>Example: Lordz2.io is a multiplayer tower defense game set in the time of kings, lords, and wizards.</p>
                  <p>Instructions: Please describe your game in 100-200 words or more. The longer the description the more traffic your game gets. We suggest you write about the game play, controls, the scoring system, strategies, etc. Be creative! We will fill the text box with a layout to get you started, but feel free to use your own style!</p>
                </Content>
              </Block>

              <Block>
                <Checkbox.Field name='mobileTrue' form={form}>
                  <Slot name='label'>Works on Mobile</Slot>
                  <Slot name='description'>Is your gameplay experience specifically designed to work on mobile? Check this box if the answer is yes. Please note, we do check. If you check this box and your game is not optional on mobile, we may need to unpublish your game.</Slot>
                </Checkbox.Field>
              </Block>

              <Block>
                <Checkbox.Field.List name='categories' form={form}>
                  <Slot name='label'>Category</Slot>
                </Checkbox.Field.List>
              </Block>

              <Title size4>Cross Promotion</Title>

              <Block>
                <Checkbox.Field name='crossPromotion' form={form}>
                  <Slot name='label'>Cross Promotion</Slot>
                  <Slot name='description'>Do you have the iogames.space cross-promotion widget installed on your website?</Slot>
                </Checkbox.Field>
              </Block>

              <Block>
                <Checkbox.Field name='ioLink' form={form}>
                  <Slot name='label'>IO Games Link</Slot>
                  <Slot name='description'>Does your game have a link back to use which says 'io games' or something similar? Since we are linking to you, we appreciate a mutual link back to us! The link can be anywhere on your site, and doesn' need to be in the footer.</Slot>
                </Checkbox.Field>
              </Block>

              <Block>
                <Input.Field name='amazonLink' type={Input.ANY} form={form}>
                  <Slot name='label'>Amazon Store Link</Slot>
                </Input.Field>
              </Block>

              <Block>
                <Input.Field name='googlePlayLink' type={Input.ANY} form={form}>
                  <Slot name='label'>Google Play Store Link</Slot>
                </Input.Field>
              </Block>

              <Block>
                <Input.Field name='appleAppLink' type={Input.ANY} form={form}>
                  <Slot name='label'>Apple App Store Link</Slot>
                </Input.Field>
              </Block>

              <Button 
                primary 
                submit 
                disabled={form.hasErrors}
                loading={loading}
              >
                Submit Game
              </Button>

            </Form>
          </Block>
        </Box>
      </DeveloperLayout>
    </>
  )
});
